/** Clients' payments (F36) as the API sends them. Amounts are missing for whoever may not see them. */
export const RECEIVABLES_RESOURCE = 'payments.receivables';
export const PAYMENTS_SETTINGS_RESOURCE = 'payments.settings';
export const PAYMENTS_KEY = ['payments'] as const;

export type Method = 'przelew' | 'gotowka' | 'barter';
export const METHODS: Record<Method, string> = { przelew: 'Przelew', gotowka: 'Gotówka', barter: 'Barter' };
/** How an installment is settled (owner 2026-10-22); only "faktura" and "barter" are invoiced by us. */
export type Form = 'faktura' | 'przelew' | 'umowa' | 'gotowka' | 'faktura_inna' | 'barter';
export const FORMS: Record<Form, string> = { faktura: 'Faktura (przelew)', przelew: 'Przelew bez faktury', umowa: 'Umowa o dzieło', gotowka: 'Gotówka', faktura_inna: 'Faktura innego podmiotu (gotówka)', barter: 'Barter' };
/** How the money of a form comes: the method a confirmation starts with. */
export const METHOD_OF_FORM: Record<Form, Method> = { faktura: 'przelew', przelew: 'przelew', umowa: 'przelew', gotowka: 'gotowka', faktura_inna: 'gotowka', barter: 'barter' };
/** The note of a form: who issues the invoice ("Podmiot", required) or the contract ("Umowa", optional). */
export const FORM_NOTE_LABEL: Partial<Record<Form, string>> = { faktura_inna: 'Podmiot', umowa: 'Umowa (numer)' };
/** "Faktura innego podmiotu (gotówka) · Agencja Echo". */
export const formText = (r: { form?: Form; formNote?: string }) => (r.form ? [FORMS[r.form], r.formNote].filter(Boolean).join(' · ') : '');
export type InstallmentKind = 'zaliczka' | 'reszta' | 'inna';
export const KINDS: Record<InstallmentKind, string> = { zaliczka: 'Zaliczka', reszta: 'Reszta', inna: 'Inna' };
export type ReceivableState = 'otwarta' | 'zaplacona' | 'anulowana';

export type Receivable = {
  id: string;
  /** "manual": added by hand („+ Należność”), changed and deleted only in „Należności”. */
  source: 'invoice' | 'installment' | 'manual';
  sourceId: string;
  label: string;
  contractorId: string | null;
  contractorName: string;
  eventId: string | null;
  eventName: string;
  amountGrosze?: number;
  paidGrosze?: number;
  remainingGrosze?: number;
  /** F40: paid beyond what it asks now (a correction lowered the invoice, an advance was refunded). */
  refundGrosze?: number;
  dueDate: string;
  method: Method;
  /** Missing on rows from an older server. */
  form?: Form;
  formNote?: string;
  state: ReceivableState;
  overdueDays: number;
  version: number;
};
/** A payment typed by hand, shown on "Do potwierdzenia" with "Cofnij". */
export type Confirmation = { id: string; amountGrosze?: number; method: Method | 'kompensata'; paidOn: string; note: string; createdAt: string };
export type ToConfirm = Receivable & { confirmations: Confirmation[] };
export type Installment = { id: string; kind: InstallmentKind; label: string; amountGrosze?: number; dueDate: string; method: Method; form?: Form; formNote?: string; receivableId: string | null };
export type Allocation = { id: string; receivableId: string | null; transactionId: string | null; refund: boolean; amountGrosze?: number; method: Method | 'kompensata'; paidOn: string; note: string; confidence: string; createdAt: string };
export type EventPayments = {
  eventId: string;
  contractorId: string | null;
  plan: { installments: Installment[]; version: number } | null;
  receivables: Receivable[];
  allocations: Allocation[];
  paidGrosze?: number;
  openGrosze?: number;
  overdueGrosze?: number;
  contractorCredit?: number;
};

export const daysWord = (n: number) => `${n} ${n === 1 ? 'dzień' : 'dni'}`;

/** "Zaległa 9 dni", "Otwarta", "Zapłacona", "Anulowana". */
export function stateLabel(r: Pick<Receivable, 'state' | 'overdueDays'>): string {
  if (r.state === 'otwarta') return r.overdueDays > 0 ? `Zaległa ${daysWord(r.overdueDays)}` : 'Otwarta';
  return r.state === 'zaplacona' ? 'Zapłacona' : 'Anulowana';
}

/** F38: why a bank operation was suggested for a receivable / payable (spec E3 §6.4). */
export const SIGNALS: Record<string, string> = {
  numer_faktury: 'numer faktury w tytule',
  numer_ksef: 'numer KSeF w tytule',
  rachunek: 'znany rachunek',
  nip: 'NIP w tytule',
  kwota: 'zgodna kwota',
  kwota_czesciowa: 'część kwoty',
  nazwa: 'podobna nazwa',
};
/** A bank operation as bank lets the user see it (amount and the other side's account are bank's hidden fields). */
export type BankOperation = { id: string; bookingDate: string; amountGrosze?: number; counterpartyName: string; counterpartyIban?: string | null; title: string };
export type Payable = { id: string; source: 'purchase' | 'settlement'; label: string; amountGrosze?: number; paidGrosze?: number; remainingGrosze?: number; dueDate: string; state: string; version: number };
/**
 * `allocationId`: a manual transfer that may be this operation — "Połącz" links it instead of a second payment.
 * `check`: sent back with "Zatwierdź"; 409 when the amounts changed meanwhile. `score` is missing without the amounts.
 */
export type ReviewSuggestion = { id: string; receivable?: Receivable; payable?: Payable; refund: boolean; allocationId: string | null; score?: number; signals: string[]; source: 'reguly' | 'model'; reason: string; check: string };
/** `unallocatedGrosze`: what is left of the operation to allocate (missing without the amount). */
export type ReviewItem = { transaction: BankOperation; unallocatedGrosze?: number; suggestions: ReviewSuggestion[] };
export type DismissedItem = { transaction: BankOperation; dismissReason: string; dismissedAt: string };
